/**
 * WP-B — Authenticated server entry points for the durable Chat surface.
 *
 * The actor is NEVER accepted from the caller: `requireSupabaseAuth` validates
 * the bearer token against Supabase Auth and puts the verified `userId` on the
 * context. The privileged adapter is imported INSIDE each handler so the
 * service-role client never enters a client-reachable module graph.
 *
 * Responses are the stable, non-sensitive Chat outcome vocabulary — never a
 * provider message, SQL text, digest or raw exception.
 */

import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { CompileDisposition } from "@/lib/ai/compile";
import {
  appendAssistantMessage,
  appendUserMessage,
  createAssistantConversation,
  loadConversation,
  loadConversationMessages,
  referenceLoop,
  type ChatOutcome,
} from "./chatCommand";
import type { ConversationRecord, LoopReferenceRecord, MessageRecord } from "./chatPort";
import {
  validateAppendMessageInput,
  validateConversationIdInput,
  validateCreateConversationInput,
  validateReferenceLoopInput,
} from "./chatRequest";
import type { ProposalRecord } from "./proposalView";
import type { ConversationResearchRecord } from "./chatAdapter.server";

export const createChatConversation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateCreateConversationInput)
  .handler(async ({ data, context }): Promise<ChatOutcome<ConversationRecord>> => {
    const { supabaseChatPort } = await import("./chatAdapter.server");
    return createAssistantConversation(supabaseChatPort, { userId: context.userId }, data);
  });

export const listChatConversations = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ChatOutcome<readonly ConversationRecord[]>> => {
    const { listWorkspaceConversations } = await import("./chatAdapter.server");
    const { classifyChatError, isRetryableChatReason } = await import("./chatErrors");
    try {
      return { ok: true, value: await listWorkspaceConversations(context.userId) };
    } catch (error) {
      const reason = classifyChatError(error);
      return { ok: false, reason, retryable: isRetryableChatReason(reason) };
    }
  });

export const loadChatConversation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateConversationIdInput)
  .handler(async ({ data, context }): Promise<ChatOutcome<ConversationRecord>> => {
    const { supabaseChatPort } = await import("./chatAdapter.server");
    return loadConversation(supabaseChatPort, { userId: context.userId }, data.conversationId);
  });

export const loadChatMessages = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateConversationIdInput)
  .handler(async ({ data, context }): Promise<ChatOutcome<readonly MessageRecord[]>> => {
    const { supabaseChatPort } = await import("./chatAdapter.server");
    return loadConversationMessages(
      supabaseChatPort,
      { userId: context.userId },
      data.conversationId,
    );
  });

export const sendChatUserMessage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateAppendMessageInput)
  .handler(async ({ data, context }): Promise<ChatOutcome<MessageRecord>> => {
    const { supabaseChatPort } = await import("./chatAdapter.server");
    return appendUserMessage(supabaseChatPort, { userId: context.userId }, data);
  });

/**
 * Assistant content is appended by this trusted boundary AFTER the user's
 * message is already durable, so a compiler failure can never take the user's
 * message with it. Nothing here carries authority over Loop state.
 */
export const recordChatAssistantMessage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateAppendMessageInput)
  .handler(async ({ data, context }): Promise<ChatOutcome<MessageRecord>> => {
    const { supabaseChatPort } = await import("./chatAdapter.server");
    return appendAssistantMessage(supabaseChatPort, { userId: context.userId }, data);
  });

export const referenceChatLoop = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateReferenceLoopInput)
  .handler(async ({ data, context }): Promise<ChatOutcome<LoopReferenceRecord>> => {
    const { supabaseChatPort } = await import("./chatAdapter.server");
    return referenceLoop(supabaseChatPort, { userId: context.userId }, data);
  });

/**
 * One governed conversation turn (WP-AI1, Real AI / PEE Contract v0.1.1).
 *
 * Order matters and is not negotiable: the user's message is made durable
 * FIRST, so a compiler failure can never take it with it. The assistant
 * reply, the content-free provenance row and the PEE-validated proposal are
 * then recorded in ONE transaction by `record_ai_turn_command`; the proposal
 * is untrusted structured content and carries no Loop authority whatsoever.
 */
export const submitChatTurn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateAppendMessageInput)
  .handler(async ({ data, context }): Promise<ChatOutcome<ChatTurn>> => {
    const { supabaseChatPort, nameConversationFromFirstMessage } =
      await import("./chatAdapter.server");
    const { runAiTurn } = await import("@/lib/ai/aiTurn.server");
    const actor = { userId: context.userId };

    const user = await appendUserMessage(supabaseChatPort, actor, data);
    if (!user.ok) return user;

    // A conversation the person never named takes its name from what they wrote.
    try {
      await nameConversationFromFirstMessage(context.userId, data.conversationId, data.content);
    } catch {
      // Cosmetic only: a naming failure must never fail a durable turn.
    }

    const claims = context.claims as { is_anonymous?: unknown } | undefined;
    const turn = await runAiTurn({
      actorUserId: context.userId,
      isGuest: claims?.is_anonymous === true,
      conversationId: data.conversationId,
      sourceMessageId: user.value.id,
    });
    if (!turn.ok) return turn;

    return {
      ok: true,
      value: {
        userMessage: user.value,
        assistantMessage: turn.assistantMessage,
        proposal: turn.proposal,
        disposition: turn.disposition,
      },
    };
  });

export interface ChatTurn {
  readonly userMessage: MessageRecord;
  readonly assistantMessage: MessageRecord;
  readonly proposal: ProposalRecord | null;
  /** Categorical compile disposition (§14) — never a provider message. */
  readonly disposition: CompileDisposition;
}

export interface ChatTranscript {
  readonly conversation: ConversationRecord;
  readonly messages: readonly MessageRecord[];
  readonly proposals: readonly ProposalRecord[];
  readonly references: readonly { readonly loopId: string; readonly messageId: string | null }[];
  /** PEE v1.3 research provenance, keyed to assistant messages. */
  readonly research: readonly ConversationResearchRecord[];
}

/** One authoritative read of everything a conversation screen renders. */
export const loadChatTranscript = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateConversationIdInput)
  .handler(async ({ data, context }): Promise<ChatOutcome<ChatTranscript>> => {
    const {
      supabaseChatPort,
      listConversationProposals,
      listConversationReferences,
      listConversationResearch,
    } = await import("./chatAdapter.server");
    const { classifyChatError, isRetryableChatReason } = await import("./chatErrors");
    const actor = { userId: context.userId };

    const conversation = await loadConversation(supabaseChatPort, actor, data.conversationId);
    if (!conversation.ok) return conversation;
    const messages = await loadConversationMessages(supabaseChatPort, actor, data.conversationId);
    if (!messages.ok) return messages;

    try {
      const proposals = await listConversationProposals(context.userId, data.conversationId);
      const references = await listConversationReferences(context.userId, data.conversationId);
      const research = await listConversationResearch(context.userId, data.conversationId);
      return {
        ok: true,
        value: {
          conversation: conversation.value,
          messages: messages.value,
          proposals,
          references: [...references],
          research: [...research],
        },
      };
    } catch (error) {
      const reason = classifyChatError(error);
      return { ok: false, reason, retryable: isRetryableChatReason(reason) };
    }
  });

// WP-C1: the post-hoc `confirmChatProposal` path is retired. Confirmation is
// the canonicalization command itself (src/lib/loops/canonicalization), which
// confirms the proposal, creates the Loop and records the conversation lineage
// in one transaction. Chat holds no authority over that step.
